import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const GET_ACCOUNTS_FOR_SELECT = gql`
  query GetAccountsForCompanyName($name: String, $first: Int) {
    accounts(name: $name, first: $first) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const GET_CONTACTS_FOR_SELECT = gql`
  query GetContactsForCompanyName($name: String, $first: Int) {
    contacts(name: $name, first: $first) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export type EditCompanyNameFieldProps = MultiSelectProps | SingleSelectProps;

interface BaseEditCompanyNameFieldProps {
  excludeContactId?: string;
  fieldClassName?: string;
  label: string;
  labelClassName?: string;
  placeholder?: string;
}

interface CompanyNameOption {
  id: string;
  name: string;
}

interface MultiSelectProps extends BaseEditCompanyNameFieldProps {
  multiSelect: true;
  onChange: (values: CompanyNameOption[]) => void;
  value: CompanyNameOption[];
}

interface SingleSelectProps extends BaseEditCompanyNameFieldProps {
  multiSelect?: false;
  onChange: (id: null | string, name: string) => void;
  value: { id: string; name: string } | null;
}

export function EditCompanyNameField(props: EditCompanyNameFieldProps) {
  const {
    excludeContactId,
    fieldClassName,
    label,
    labelClassName,
    placeholder = 'Select an account or contact'
  } = props;

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const triggerId = useId();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const variables = {
    first: 50,
    name: debouncedSearchTerm || undefined
  };

  const { data: accountsData, loading: loadingAccounts } = useQuery<{
    accounts?: { edges: { node: CompanyNameOption }[] };
  }>(GET_ACCOUNTS_FOR_SELECT, {
    fetchPolicy: 'cache-first',
    variables
  });

  const { data: contactsData, loading: loadingContacts } = useQuery<{
    contacts?: { edges: { node: CompanyNameOption }[] };
  }>(GET_CONTACTS_FOR_SELECT, {
    fetchPolicy: 'cache-first',
    variables
  });

  const accounts: CompanyNameOption[] =
    accountsData?.accounts?.edges.map(edge => edge.node) ?? [];
  const contacts: CompanyNameOption[] =
    contactsData?.contacts?.edges
      .map(edge => edge.node)
      .filter(c => c.id !== excludeContactId) ?? [];

  const loading = loadingAccounts || loadingContacts;

  // --- Multi-select mode ---
  if (props.multiSelect) {
    const { onChange, value } = props;
    const selectedIds = new Set(value.map(v => v.id));

    const handleToggle = (option: CompanyNameOption) => {
      if (selectedIds.has(option.id)) {
        onChange(value.filter(v => v.id !== option.id));
      } else {
        onChange([...value, option]);
      }
    };

    const handleRemove = (id: string) => {
      onChange(value.filter(v => v.id !== id));
    };

    return (
      <Field className={cn('gap-1.5', fieldClassName)}>
        <FieldLabel
          className={labelClassName}
          htmlFor={triggerId}
        >
          {label}
        </FieldLabel>
        <Popover
          modal={true}
          onOpenChange={setOpen}
          open={open}
        >
          <PopoverTrigger asChild>
            <Button
              aria-expanded={open}
              className={cn(
                'dark:bg-input/30 dark:hover:bg-input/50 h-auto min-h-9 w-full justify-between bg-transparent font-normal',
                value.length === 0 && 'text-gray-500'
              )}
              id={triggerId}
              role="combobox"
              variant="outline"
            >
              <span className="flex flex-1 flex-wrap gap-1 overflow-hidden">
                {value.length === 0
                  ? placeholder
                  : value.map(v => (
                      <Badge
                        className="gap-1 pr-1"
                        key={v.id}
                        variant="secondary"
                      >
                        <span className="max-w-[120px] truncate">{v.name}</span>
                        <span
                          className="hover:bg-muted-foreground/20 rounded-sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemove(v.id);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              handleRemove(v.id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <X className="h-3 w-3" />
                        </span>
                      </Badge>
                    ))}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput
                onValueChange={setSearchTerm}
                placeholder="Search accounts or contacts..."
                value={searchTerm}
              />
              <CommandList>
                {loading ? (
                  <div className="py-6 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : (
                  <>
                    {value.length > 0 && (
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            onChange([]);
                          }}
                          value="__clear_all__"
                        >
                          <X className="mr-2 h-4 w-4" />
                          <span className="text-gray-500">Clear all</span>
                        </CommandItem>
                      </CommandGroup>
                    )}
                    <CommandGroup heading="Accounts">
                      {accounts.map(account => (
                        <CommandItem
                          key={account.id}
                          onSelect={() => {
                            handleToggle(account);
                          }}
                          value={account.id}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedIds.has(account.id)
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          {account.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Contacts">
                      {contacts.map(contact => (
                        <CommandItem
                          key={contact.id}
                          onSelect={() => {
                            handleToggle(contact);
                          }}
                          value={contact.id}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedIds.has(contact.id)
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          {contact.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </Field>
    );
  }

  // --- Single-select mode (default) ---
  const { onChange, value } = props;

  return (
    <Field className={cn('gap-1.5', fieldClassName)}>
      <FieldLabel
        className={labelClassName}
        htmlFor={triggerId}
      >
        {label}
      </FieldLabel>
      <Popover
        modal={true}
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={`dark:bg-input/30 dark:hover:bg-input/50 h-9 w-full justify-between bg-transparent font-normal ${!value?.name ? 'text-gray-500' : ''}`}
            id={triggerId}
            role="combobox"
            variant="outline"
          >
            {value?.name ?? placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearchTerm}
              placeholder="Search accounts or contacts..."
              value={searchTerm}
            />
            <CommandList>
              {loading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ) : (
                <>
                  <CommandGroup heading="Accounts">
                    <CommandItem
                      onSelect={() => {
                        onChange(null, '');
                        setOpen(false);
                      }}
                      value="__none__"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          !value?.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="text-gray-500">None</span>
                    </CommandItem>
                    {accounts.map(account => (
                      <CommandItem
                        key={account.id}
                        onSelect={() => {
                          onChange(account.id, account.name);
                          setOpen(false);
                        }}
                        value={account.id}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value?.id === account.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {account.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandGroup heading="Contacts">
                    {contacts.map(contact => (
                      <CommandItem
                        key={contact.id}
                        onSelect={() => {
                          onChange(contact.id, contact.name);
                          setOpen(false);
                        }}
                        value={contact.id}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value?.id === contact.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {contact.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Field>
  );
}
